import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Autocomplete,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Chip,
  ThemeProvider,
  createTheme,
  IconButton,
  CircularProgress,
  Skeleton,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

// Import Modal Components
import AddFeatureModal from "./AddFeatureModal";
import CreateTextStatusModal from "./CreateTextStatusModalForMem";
import CreateMembershipModal from "./CreateMembershipModal";
import AddNewFeatureModal from "./AddNewFeatureModal";
import DeleteConfirmModal from "./DeleteConfirmModal";
import { BASE_URL } from "../../../../services/configUrls";

// Zoho Books-inspired theme
const theme = createTheme({
  typography: {
    fontFamily: '"Poppins", "Lato", "Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 13,
  },
  palette: {
    primary: {
      main: "#2D7FF9",
      light: "#5B9EFA",
      dark: "#1E5FD9",
    },
    success: {
      main: "#0BBE87",
    },
    error: {
      main: "#E85858",
    },
    background: {
      default: "#F9FAFB",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#212529",
      secondary: "#6C757D",
    },
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            backgroundColor: "#FFFFFF",
            "& fieldset": {
              borderColor: "#D1D5DB",
            },
            "&:hover fieldset": {
              borderColor: "#9CA3AF",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#2D7FF9",
              borderWidth: "1px",
            },
          },
          "& .MuiInputBase-input": {
            fontSize: "13px",
            padding: "10px 12px",
          },
          "& .MuiInputLabel-root": {
            fontSize: "13px",
            color: "#6C757D",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontSize: "13px",
          fontWeight: 500,
          padding: "8px 16px",
          borderRadius: "4px",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        },
        contained: {
          "&:hover": {
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: "13px",
          padding: "12px 16px",
          borderBottom: "1px solid #E5E7EB",
        },
        head: {
          fontWeight: 600,
          color: "#374151",
          backgroundColor: "#F9FAFB",
          borderBottom: "2px solid #E5E7EB",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          borderRadius: "6px",
        },
      },
    },
  },
});

const getStatusColor = (statusValue, type) => {
  if (type === "Count" || type === "count") {
    return { bg: "#E0E7FF", color: "#3730A3" };
  }
  return { bg: "#E0E7FF", color: "#3730A3" };
};

const getStatusDisplay = (statusValue, type, symbol) => {
  if (type === "Count" || type === "count") {
    return statusValue;
  }
  if (symbol) {
    return `${symbol} ${statusValue}`;
  }
  return statusValue;
};

const MembershipFormSystem = () => {
  const [existingMemberships, setExistingMemberships] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [features, setFeatures] = useState([]);
  const [openAddFeatureDialog, setOpenAddFeatureDialog] = useState(false);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [openCreateMembershipDialog, setOpenCreateMembershipDialog] =
    useState(false);
  const [newMembershipName, setNewMembershipName] = useState("");
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    index: null,
  });
  const [openAddNewFeatureDialog, setOpenAddNewFeatureDialog] = useState(false);
  const [newFeatureNameInput, setNewFeatureNameInput] = useState("");
  const [newFeatureTypeInput, setNewFeatureTypeInput] = useState("text");
  const [createMembershipLoading, setCreateMembershipLoading] = useState(false);
  const [openCreateTextStatusDialog, setOpenCreateTextStatusDialog] =
    useState(false);
  const [newStatusText, setNewStatusText] = useState("");
  const [newStatusSymbol, setNewStatusSymbol] = useState("");
  const [currentEditingFeature, setCurrentEditingFeature] = useState(null);
  const [editingStatus, setEditingStatus] = useState(null);
  const [tempStatusValue, setTempStatusValue] = useState("");
  const [featureSearchQuery, setFeatureSearchQuery] = useState("");
  const [featureTypes, setFeatureTypes] = useState({});
  const [featureStatusValues, setFeatureStatusValues] = useState({});
  const [addFeatureModalKey, setAddFeatureModalKey] = useState(0);
  const [apiStatusOptions, setApiStatusOptions] = useState([]);
  const [updateLoading, setUpdateLoading] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // NEW ERROR STATES FOR COUNT VALIDATION
  const [countError, setCountError] = useState(false);
  const [countErrorMessage, setCountErrorMessage] = useState("");

  useEffect(() => {
    fetchMemberships();
    fetchStatusOptions();
  }, []);

  const fetchStatusOptions = async () => {
    try {
      const accessToken = localStorage.getItem("accessToken");

      const response = await fetch(`${BASE_URL}/accounts/all-status-options`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const result = await response.json();

      if (Array.isArray(result)) {
        const transformedStatuses = result.map((status) => ({
          id: status.id,
          text: status.status_text,
          symbol: status.symbol,
          color: { bg: "#E0E7FF", color: "#3730A3" },
        }));
        setApiStatusOptions(transformedStatuses);
      }
    } catch (error) {
      console.error("Error fetching status options:", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch status options",
        severity: "error",
      });
    }
  };

  const fetchMemberships = async (currentMembershipId = null) => {
    setLoading(true);
    try {
      const accessToken = localStorage.getItem("accessToken");

      const response = await fetch(`${BASE_URL}/accounts/list`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });

      const result = await response.json();

      if (result.data && Array.isArray(result.data)) {
        const transformedMemberships = result.data.map((membership) => ({
          id: membership.id,
          name: membership.name,
          membershipFee: membership.membership_fee,
          validityDays: membership.validity_days,
          features: membership.features.map((feature) => ({
            featureId: feature.feature_id,
            name: feature.name,
            type: feature.type.toLowerCase(),
            statusValue: feature.statusValue,
            symbol: feature.symbol,
          })),
          isNew: membership.isNew || false,
        }));

        setExistingMemberships(transformedMemberships);

        if (currentMembershipId) {
          const updatedMembership = transformedMemberships.find(
            (m) => m.id === currentMembershipId
          );
          if (updatedMembership) {
            setSelectedMembership(updatedMembership);
            setFeatures(updatedMembership.features);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching memberships:", error);
      setSnackbar({
        open: true,
        message: "Failed to fetch memberships",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMembershipChange = (event, newValue) => {
    if (newValue?.isCreateNew) {
      setOpenCreateMembershipDialog(true);
      setInputValue("");
      return;
    }

    if (typeof newValue === "string") {
      setSelectedMembership({
        id: null,
        name: newValue,
        features: [],
        isNew: true,
      });
      setFeatures([]);
    } else if (newValue && newValue.inputValue) {
      setSelectedMembership({
        id: null,
        name: newValue.inputValue,
        features: [],
        isNew: true,
      });
      setFeatures([]);
    } else {
      setSelectedMembership(newValue);
      setFeatures(newValue ? [...newValue.features] : []);
    }
  };

  const handleFeatureTypeChange = (featureName, type) => {
    setFeatureTypes((prev) => ({
      ...prev,
      [featureName]: type,
    }));

    if (type === "text") {
      setFeatureStatusValues((prev) => ({
        ...prev,
        [featureName]: "Yes",
      }));
    } else if (type === "count") {
      setFeatureStatusValues((prev) => ({
        ...prev,
        [featureName]: "0",
      }));
    }
  };

  const handleFeatureStatusValueChange = (featureName, value) => {
    setFeatureStatusValues((prev) => ({
      ...prev,
      [featureName]: value,
    }));
  };

  const handleFeatureToggle = (featureName) => {
    setSelectedFeatures((prev) => {
      const newSelection = prev.includes(featureName)
        ? prev.filter((name) => name !== featureName)
        : [...prev, featureName];

      if (!newSelection.includes(featureName)) {
        setFeatureTypes((prevTypes) => {
          const newTypes = { ...prevTypes };
          delete newTypes[featureName];
          return newTypes;
        });
        setFeatureStatusValues((prevValues) => {
          const newValues = { ...prevValues };
          delete newValues[featureName];
          return newValues;
        });
      } else if (!featureTypes[featureName]) {
        setFeatureTypes((prevTypes) => ({
          ...prevTypes,
          [featureName]: "text",
        }));
        setFeatureStatusValues((prevValues) => ({
          ...prevValues,
          [featureName]: "Yes",
        }));
      }

      return newSelection;
    });
  };

  const handleStatusEdit = (index, currentType, currentValue) => {
    setEditingStatus(index);
    setTempStatusValue(currentValue);
    // RESET ERROR STATES WHEN OPENING EDIT
    setCountError(false);
    setCountErrorMessage("");
  };

  const handleStatusSave = async (index) => {
    const feature = features[index];

    // CHECK FOR COUNT ERROR BEFORE SAVING
    if (countError) {
      setSnackbar({
        open: true,
        message: "Please fix the errors before saving",
        severity: "error",
      });
      return;
    }

    if (feature.type === "count" && isNaN(tempStatusValue)) {
      setSnackbar({
        open: true,
        message: "Please enter a valid number for count",
        severity: "error",
      });
      return;
    }

    if (!tempStatusValue.trim()) {
      setEditingStatus(null);
      return;
    }

    setUpdateLoading(index);

    try {
      const accessToken = localStorage.getItem("accessToken");

      let payload;
      if (feature.type === "count") {
        payload = {
          membership_id: selectedMembership.id,
          feature_id: feature.featureId,
          count_value: parseInt(tempStatusValue),
        };
      } else {
        const selectedStatus = apiStatusOptions.find(
          (status) => status.text === tempStatusValue
        );

        if (!selectedStatus) {
          setSnackbar({
            open: true,
            message: "Please select a valid status from the list",
            severity: "error",
          });
          setUpdateLoading(null);
          return;
        }

        payload = {
          membership_id: selectedMembership.id,
          feature_id: feature.featureId,
          status_option_id: selectedStatus.id,
        };
      }

      console.log("Update payload:", payload);

      const response = await fetch(
        `${BASE_URL}/accounts/membership-feature-update`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      if (response.ok) {
        const updatedFeatures = [...features];
        updatedFeatures[index].statusValue = tempStatusValue;
        if (feature.type === "text") {
          const selectedStatus = apiStatusOptions.find(
            (status) => status.text === tempStatusValue
          );
          updatedFeatures[index].symbol = selectedStatus?.symbol;
        }
        setFeatures(updatedFeatures);

        setSnackbar({
          open: true,
          message: result.message || "Feature updated successfully",
          severity: "success",
        });

        setEditingStatus(null);
        setTempStatusValue("");
        // RESET ERROR STATES AFTER SUCCESSFUL SAVE
        setCountError(false);
        setCountErrorMessage("");

        await fetchMemberships(selectedMembership.id);
      } else {
        setSnackbar({
          open: true,
          message: result.message || "Failed to update feature",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error updating feature:", error);
      setSnackbar({
        open: true,
        message: "Error updating feature. Please try again.",
        severity: "error",
      });
    } finally {
      setUpdateLoading(null);
    }
  };

  const handleStatusCancel = () => {
    setEditingStatus(null);
    setTempStatusValue("");
    // RESET ERROR STATES WHEN CANCELING
    setCountError(false);
    setCountErrorMessage("");
  };

  const handleCreateTextStatus = () => {
    // This function is now handled by the modal itself
  };

  const handleCreateNewMembership = async (validityYears) => {
    if (newMembershipName.trim()) {
      setCreateMembershipLoading(true);
      try {
        const accessToken = localStorage.getItem("accessToken");

        const response = await fetch(`${BASE_URL}/accounts/create/membership`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            membership_name: newMembershipName.trim(),
            validity_years: validityYears, // Send as count only
          }),
        });

        const result = await response.json();

        if (response.ok && result.success) {
          await fetchMemberships();

          const newlyCreatedMembership = existingMemberships.find(
            (m) =>
              m.id === result.membership_id ||
              m.name === newMembershipName.trim()
          );

          if (newlyCreatedMembership) {
            setSelectedMembership(newlyCreatedMembership);
            setFeatures(newlyCreatedMembership.features || []);
            setInputValue(newlyCreatedMembership.name);
          }

          setSnackbar({
            open: true,
            message: result.message || "Membership created successfully",
            severity: "success",
          });
        } else {
          setSnackbar({
            open: true,
            message: result.message || "Failed to create membership",
            severity: "error",
          });
        }
      } catch (error) {
        console.error("Error creating membership:", error);
        setSnackbar({
          open: true,
          message: "Error creating membership. Please try again.",
          severity: "error",
        });
      } finally {
        setCreateMembershipLoading(false);
        setNewMembershipName("");
        setOpenCreateMembershipDialog(false);
      }
    }
  };

  const handleDeleteFeatureClick = (index) => {
    setDeleteConfirmDialog({ open: true, index });
  };

  const handleDeleteFeatureConfirm = async () => {
    const index = deleteConfirmDialog.index;
    const feature = features[index];

    setUpdateLoading(index);

    try {
      const accessToken = localStorage.getItem("accessToken");

      const response = await fetch(
        `${BASE_URL}/accounts/membership-feature?membership_id=${selectedMembership.id}&feature_id=${feature.featureId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const result = await response.json();

      if (response.ok) {
        const updatedFeatures = features.filter((_, i) => i !== index);
        setFeatures(updatedFeatures);

        setSnackbar({
          open: true,
          message: result.message || "Feature removed successfully",
          severity: "success",
        });

        await fetchMemberships(selectedMembership.id);
      } else {
        setSnackbar({
          open: true,
          message: result.message || "Failed to remove feature",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error removing feature:", error);
      setSnackbar({
        open: true,
        message: "Error removing feature. Please try again.",
        severity: "error",
      });
    } finally {
      setUpdateLoading(null);
      setDeleteConfirmDialog({ open: false, index: null });
    }
  };

  const handleDeleteFeatureCancel = () => {
    setDeleteConfirmDialog({ open: false, index: null });
  };

  const handleSubmit = () => {
    const formData = {
      membership: selectedMembership,
      isNewMembership: selectedMembership?.isNew || false,
      features: features,
      totalFeatures: features.length,
      typeBreakdown: features.reduce((acc, feature) => {
        acc[feature.type] = (acc[feature.type] || 0) + 1;
        return acc;
      }, {}),
    };
    console.log("Form Data:", formData);

    if (selectedMembership?.isNew) {
      const newMembershipToAdd = {
        id: selectedMembership.id,
        name: selectedMembership.name,
        features: [...features],
        isNew: false,
      };
      setExistingMemberships([...existingMemberships, newMembershipToAdd]);
    } else {
      const updatedMemberships = existingMemberships.map((m) =>
        m.id === selectedMembership.id ? { ...m, features: [...features] } : m
      );
      setExistingMemberships(updatedMemberships);
    }

    setSnackbar({
      open: true,
      message: "Membership saved successfully!",
      severity: "success",
    });

    setSelectedMembership(null);
    setFeatures([]);
    setInputValue("");
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "#F9FAFB",
          py: 4,
          px: 3,
          pb: selectedMembership ? "100px" : 4,
        }}
      >
        {loading ? (
          <Box sx={{ maxWidth: 1500, mx: "auto" }}>
            <Box sx={{ mb: 4 }}>
              <Skeleton variant="text" width={300} height={40} />
              <Skeleton variant="text" width={400} height={20} />
            </Box>

            <Paper sx={{ p: 3, mb: 3 }}>
              <Skeleton variant="text" width={200} height={30} sx={{ mb: 2 }} />
              <Skeleton
                variant="rectangular"
                width="100%"
                height={56}
                sx={{ mb: 3, borderRadius: 1 }}
              />

              <Divider sx={{ mb: 3 }} />

              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
              >
                <Skeleton variant="text" width={150} height={30} />
                <Skeleton
                  variant="rectangular"
                  width={180}
                  height={36}
                  sx={{ borderRadius: 1 }}
                />
              </Box>

              {[1, 2, 3, 4, 5].map((i) => (
                <Box key={i} sx={{ display: "flex", gap: 2, mb: 2 }}>
                  <Skeleton variant="text" width="10%" height={50} />
                  <Skeleton variant="text" width="45%" height={50} />
                  <Skeleton
                    variant="rectangular"
                    width="35%"
                    height={50}
                    sx={{ borderRadius: 1 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    width="10%"
                    height={50}
                    sx={{ borderRadius: 1 }}
                  />
                </Box>
              ))}
            </Paper>
          </Box>
        ) : (
          <Box sx={{ maxWidth: 1500, mx: "auto" }}>
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: "#212529",
                  mb: 0.5,
                }}
              >
                Create/Edit Membership
              </Typography>
              <Typography variant="body2" sx={{ color: "#6C757D" }}>
                Manage membership details - edit values only (type is fixed)
              </Typography>
            </Box>

            <Paper sx={{ p: 3, mb: 3 }}>
              <Box
                sx={{
                  mb: 4,
                  transition: "all 0.3s ease-in-out",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: "#374151",
                    mb: 1.5,
                    fontSize: "13px",
                  }}
                >
                  Membership Details
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Autocomplete
                    value={selectedMembership}
                    onChange={handleMembershipChange}
                    inputValue={inputValue}
                    onInputChange={(event, newInputValue) => {
                      setInputValue(newInputValue);
                    }}
                    loading={loading}
                    options={[
                      {
                        id: "create-new",
                        name: "Create New...",
                        isCreateNew: true,
                      },
                      ...existingMemberships,
                    ]}
                    getOptionLabel={(option) => {
                      if (typeof option === "string") {
                        return option;
                      }
                      if (option.inputValue) {
                        return option.inputValue;
                      }
                      return option.name || "";
                    }}
                    filterOptions={(options, params) => {
                      const filtered = options.filter((option) => {
                        if (option.isCreateNew) return true;
                        return option.name
                          ?.toLowerCase()
                          .includes(params.inputValue.toLowerCase());
                      });
                      return filtered;
                    }}
                    selectOnFocus
                    clearOnBlur
                    handleHomeEndKeys
                    renderOption={(props, option) => (
                      <li {...props}>
                        {option.isCreateNew ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              width: "100%",
                              py: 0.5,
                              transition: "all 0.2s ease",
                              "&:hover": {
                                transform: "translateX(4px)",
                              },
                            }}
                          >
                            <AddIcon sx={{ fontSize: 18, color: "#2D7FF9" }} />
                            <Typography
                              sx={{
                                fontSize: "13px",
                                color: "#2D7FF9",
                                fontWeight: 600,
                              }}
                            >
                              {option.name}
                            </Typography>
                          </Box>
                        ) : option.isNew ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <AddIcon sx={{ fontSize: 18, color: "#2D7FF9" }} />
                            <Typography
                              sx={{ fontSize: "13px", color: "#2D7FF9" }}
                            >
                              {option.name}
                            </Typography>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              width: "100%",
                            }}
                          >
                            <Typography sx={{ fontSize: "13px", flex: 1 }}>
                              {option.name}
                            </Typography>
                            {option.isNew && (
                              <Chip
                                label="New"
                                size="small"
                                sx={{
                                  backgroundColor: "#D1FAE5",
                                  color: "#065F46",
                                  fontWeight: 600,
                                  fontSize: "10px",
                                  height: "20px",
                                }}
                              />
                            )}
                          </Box>
                        )}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select membership"
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    sx={{ flex: 1, maxWidth: 500 }}
                  />
                  {selectedMembership?.isNew && (
                    <Chip
                      label="New Membership"
                      size="small"
                      sx={{
                        backgroundColor: "#D1FAE5",
                        color: "#065F46",
                        fontWeight: 600,
                        fontSize: "11px",
                        animation: "fadeIn 0.3s ease-in-out",
                        "@keyframes fadeIn": {
                          from: { opacity: 0, transform: "scale(0.9)" },
                          to: { opacity: 1, transform: "scale(1)" },
                        },
                      }}
                    />
                  )}
                </Box>
              </Box>

              {selectedMembership && (
                <Box
                  sx={{
                    animation: "slideIn 0.4s ease-out",
                    "@keyframes slideIn": {
                      from: {
                        opacity: 0,
                        transform: "translateY(20px)",
                      },
                      to: {
                        opacity: 1,
                        transform: "translateY(0)",
                      },
                    },
                  }}
                >
                  <Divider sx={{ mb: 3 }} />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: "#374151",
                          fontSize: "13px",
                        }}
                      >
                        Features ({features.length})
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#9CA3AF",
                          fontSize: "11px",
                          fontStyle: "italic",
                        }}
                      >
                        Click on status to edit values (type cannot be changed)
                      </Typography>
                    </Box>
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => setOpenAddFeatureDialog(true)}
                      size="small"
                      variant="outlined"
                      sx={{
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 8px rgba(45, 127, 249, 0.2)",
                        },
                      }}
                    >
                      Add New Feature
                    </Button>
                  </Box>

                  {features.length === 0 ? (
                    <Box
                      sx={{
                        p: 6,
                        textAlign: "center",
                        border: "2px dashed #E5E7EB",
                        borderRadius: "6px",
                        backgroundColor: "#FAFBFC",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: "#6C757D", mb: 2, fontSize: "13px" }}
                      >
                        No features added yet
                      </Typography>
                      <Button
                        startIcon={<AddIcon />}
                        onClick={() => setOpenAddFeatureDialog(true)}
                        variant="contained"
                        size="small"
                        sx={{
                          transition: "all 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: "0 6px 12px rgba(45, 127, 249, 0.3)",
                          },
                        }}
                      >
                        Add Your First Feature
                      </Button>
                    </Box>
                  ) : (
                    <TableContainer
                      component={Paper}
                      variant="outlined"
                      sx={{ border: "1px solid #E5E7EB" }}
                    >
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: "10%" }}>#</TableCell>
                            <TableCell sx={{ width: "45%" }}>
                              Feature Name
                            </TableCell>
                            <TableCell sx={{ width: "35%" }}>Status</TableCell>
                            <TableCell
                              sx={{ width: "10%", textAlign: "center" }}
                            >
                              Action
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {features.map((feature, index) => (
                            <TableRow
                              key={index}
                              sx={{
                                "&:hover": {
                                  backgroundColor: "#F9FAFB",
                                },
                                animation: "fadeInRow 0.3s ease-out",
                                animationDelay: `${index * 0.05}s`,
                                animationFillMode: "backwards",
                                "@keyframes fadeInRow": {
                                  from: {
                                    opacity: 0,
                                    transform: "translateX(-10px)",
                                  },
                                  to: {
                                    opacity: 1,
                                    transform: "translateX(0)",
                                  },
                                },
                              }}
                            >
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "13px",
                                    color: "#6C757D",
                                    fontWeight: 600,
                                  }}
                                >
                                  {index + 1}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "13px",
                                    color: "#212529",
                                    fontWeight: 500,
                                  }}
                                >
                                  {feature.name}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {editingStatus === index ? (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      gap: 1,
                                      alignItems: "center",
                                    }}
                                  >
                                    {/* UPDATED COUNT TEXTFIELD WITH ERROR HANDLING */}
                                    {feature.type === "count" ? (
                                      <Box>
                                        <TextField
                                          autoFocus
                                          size="small"
                                          type="number"
                                          value={tempStatusValue}
                                          error={countError}
                                          helperText={
                                            countError ? countErrorMessage : ""
                                          }
                                          onChange={(e) => {
                                            const newValue = e.target.value;

                                            setTempStatusValue(newValue);

                                            const numValue = parseInt(newValue);
                                            if (
                                              newValue === "" ||
                                              isNaN(numValue)
                                            ) {
                                              setCountError(true);
                                              setCountErrorMessage(
                                                "Please enter a valid count"
                                              );
                                            } else if (numValue < 1) {
                                              setCountError(true);
                                              setCountErrorMessage(
                                                "Count must be at least 1"
                                              );
                                            } else {
                                              setCountError(false);
                                              setCountErrorMessage("");
                                            }
                                          }}
                                          onKeyDown={(e) => {
                                            if (
                                              e.key === "-" ||
                                              e.key === "e" ||
                                              e.key === "E" ||
                                              e.key === "+"
                                            ) {
                                              e.preventDefault();
                                            }
                                          }}
                                          placeholder="Enter count"
                                          inputProps={{
                                            min: 1,
                                          }}
                                          sx={{
                                            width: 150,
                                            "& .MuiOutlinedInput-root": {
                                              fontSize: "13px",
                                            },
                                            "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                                              {
                                                WebkitAppearance: "none",
                                                margin: 0,
                                              },
                                            "& input[type=number]": {
                                              MozAppearance: "textfield",
                                            },
                                          }}
                                        />
                                      </Box>
                                    ) : (
                                      <Autocomplete
                                        freeSolo
                                        autoFocus
                                        options={apiStatusOptions
                                          .sort((a, b) => b.id - a.id)
                                          .map((st) => st.text)}
                                        value={
                                          tempStatusValue === "Create New..."
                                            ? ""
                                            : tempStatusValue
                                        }
                                        onChange={(event, newValue) => {
                                          if (newValue) {
                                            setTempStatusValue(newValue);
                                          }
                                        }}
                                        onInputChange={(
                                          event,
                                          newInputValue,
                                          reason
                                        ) => {
                                          if (reason === "input") {
                                            setTempStatusValue(newInputValue);
                                          }
                                        }}
                                        ListboxProps={{
                                          style: {
                                            maxHeight: "250px",
                                            padding: 0,
                                          },
                                        }}
                                        ListboxComponent={React.forwardRef(
                                          (props, ref) => {
                                            const { children, ...other } =
                                              props;
                                            return (
                                              <ul
                                                {...other}
                                                ref={ref}
                                                style={{
                                                  padding: 0,
                                                  margin: 0,
                                                }}
                                              >
                                                {/* Sticky "Create New" option */}
                                                <Box
                                                  component="li"
                                                  onClick={() => {
                                                    setCurrentEditingFeature(
                                                      index
                                                    );
                                                    setOpenCreateTextStatusDialog(
                                                      true
                                                    );
                                                  }}
                                                  sx={{
                                                    position: "sticky",
                                                    top: 0,
                                                    zIndex: 10,
                                                    backgroundColor: "#FFFFFF",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1,
                                                    padding: "8px 14px",
                                                    cursor: "pointer",
                                                    borderBottom:
                                                      "1px solid #E5E7EB",
                                                    listStyle: "none",
                                                    "&:hover": {
                                                      backgroundColor:
                                                        "#F0F9FF",
                                                    },
                                                  }}
                                                >
                                                  <AddIcon
                                                    sx={{
                                                      fontSize: 16,
                                                      color: "#2D7FF9",
                                                    }}
                                                  />
                                                  <Typography
                                                    sx={{
                                                      fontSize: "13px",
                                                      color: "#2D7FF9",
                                                      fontWeight: 600,
                                                    }}
                                                  >
                                                    Create New...
                                                  </Typography>
                                                </Box>
                                                {/* Scrollable status list */}
                                                {children}
                                              </ul>
                                            );
                                          }
                                        )}
                                        renderOption={(props, option) => {
                                          const statusObj =
                                            apiStatusOptions.find(
                                              (st) => st.text === option
                                            );
                                          return (
                                            <li {...props} key={option}>
                                              <Typography
                                                sx={{ fontSize: "13px" }}
                                              >
                                                {statusObj && statusObj.symbol
                                                  ? `${statusObj.symbol} ${statusObj.text}`
                                                  : option}
                                              </Typography>
                                            </li>
                                          );
                                        }}
                                        renderInput={(params) => (
                                          <TextField
                                            {...params}
                                            size="small"
                                            placeholder="Select or type"
                                            sx={{
                                              "& .MuiOutlinedInput-root": {
                                                fontSize: "13px",
                                              },
                                            }}
                                          />
                                        )}
                                        sx={{ flex: 1, minWidth: 200 }}
                                      />
                                    )}

                                    {/* UPDATED SAVE BUTTON WITH DISABLED STATE */}
                                    <IconButton
                                      size="small"
                                      onClick={() => handleStatusSave(index)}
                                      disabled={
                                        countError || updateLoading === index
                                      }
                                      sx={{
                                        color: countError
                                          ? "grey.400"
                                          : "success.main",
                                        "&:hover": {
                                          bgcolor: countError
                                            ? "transparent"
                                            : "success.light",
                                        },
                                        cursor: countError
                                          ? "not-allowed"
                                          : "pointer",
                                        transition: "all 0.2s ease",
                                        "&:hover": {
                                          transform: countError
                                            ? "none"
                                            : "scale(1.1)",
                                        },
                                      }}
                                    >
                                      {updateLoading === index ? (
                                        <CircularProgress
                                          size={18}
                                          color="success"
                                        />
                                      ) : (
                                        <CheckIcon sx={{ fontSize: 18 }} />
                                      )}
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={handleStatusCancel}
                                      color="error"
                                      disabled={updateLoading === index}
                                      sx={{
                                        transition: "all 0.2s ease",
                                        "&:hover": {
                                          transform: "scale(1.1)",
                                        },
                                      }}
                                    >
                                      <CloseIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </Box>
                                ) : (
                                  <Box
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      px: 2,
                                      py: 1,
                                      borderRadius: "4px",
                                      backgroundColor: getStatusColor(
                                        feature.statusValue,
                                        feature.type
                                      ).bg,
                                      color: getStatusColor(
                                        feature.statusValue,
                                        feature.type
                                      ).color,
                                      fontWeight: 500,
                                      fontSize: "13px",
                                      cursor: "pointer",
                                      transition: "all 0.2s ease",
                                      "&:hover": {
                                        opacity: 0.8,
                                        transform: "translateY(-1px)",
                                      },
                                    }}
                                    onClick={() =>
                                      handleStatusEdit(
                                        index,
                                        feature.type,
                                        feature.statusValue
                                      )
                                    }
                                  >
                                    {getStatusDisplay(
                                      feature.statusValue,
                                      feature.type,
                                      feature.symbol
                                    )}
                                  </Box>
                                )}
                              </TableCell>
                              <TableCell sx={{ textAlign: "center" }}>
                                <Button
                                  size="small"
                                  onClick={() =>
                                    handleDeleteFeatureClick(index)
                                  }
                                  color="error"
                                  variant="text"
                                  startIcon={
                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                  }
                                  sx={{
                                    fontSize: "12px",
                                    padding: "4px 8px",
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                      transform: "scale(1.05)",
                                    },
                                  }}
                                >
                                  Remove
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}
            </Paper>
          </Box>
        )}
      </Box>

      <AddFeatureModal
        key={addFeatureModalKey}
        open={openAddFeatureDialog}
        onClose={() => {
          setOpenAddFeatureDialog(false);
          setSelectedFeatures([]);
          setFeatureTypes({});
          setFeatureStatusValues({});
          setFeatureSearchQuery("");
        }}
        features={features}
        selectedFeatures={selectedFeatures}
        onFeatureToggle={handleFeatureToggle}
        featureTypes={featureTypes}
        featureStatusValues={featureStatusValues}
        onFeatureTypeChange={handleFeatureTypeChange}
        onFeatureStatusValueChange={handleFeatureStatusValueChange}
        onOpenCreateTextStatus={() => setOpenCreateTextStatusDialog(true)}
        onOpenAddNewFeature={() => setOpenAddNewFeatureDialog(true)}
        featureSearchQuery={featureSearchQuery}
        onSearchQueryChange={setFeatureSearchQuery}
        setCurrentEditingFeature={setCurrentEditingFeature}
        membershipId={selectedMembership?.id}
        onRefreshMembership={(membershipId) => fetchMemberships(membershipId)}
      />

      <CreateTextStatusModal
        open={openCreateTextStatusDialog}
        onClose={() => {
          setOpenCreateTextStatusDialog(false);
          if (editingStatus !== null) {
            const feature = features[editingStatus];
            setTempStatusValue(feature.statusValue);
          }
        }}
        newStatusText={newStatusText}
        setNewStatusText={setNewStatusText}
        newStatusSymbol={newStatusSymbol}
        setNewStatusSymbol={setNewStatusSymbol}
        onCreateTextStatus={handleCreateTextStatus}
        onRefreshStatusOptions={fetchStatusOptions}
        onShowSnackbar={setSnackbar}
        onResetTempValue={(newValue) => {
          if (newValue) {
            setTempStatusValue(newValue);
          } else if (editingStatus !== null) {
            const feature = features[editingStatus];
            setTempStatusValue(feature.statusValue);
          }
        }}
      />

      <CreateMembershipModal
        open={openCreateMembershipDialog}
        onClose={() => setOpenCreateMembershipDialog(false)}
        newMembershipName={newMembershipName}
        setNewMembershipName={setNewMembershipName}
        onCreateMembership={handleCreateNewMembership}
        loading={createMembershipLoading}
      />

      <AddNewFeatureModal
        open={openAddNewFeatureDialog}
        onClose={() => setOpenAddNewFeatureDialog(false)}
        newFeatureNameInput={newFeatureNameInput}
        setNewFeatureNameInput={setNewFeatureNameInput}
        newFeatureTypeInput={newFeatureTypeInput}
        setNewFeatureTypeInput={setNewFeatureTypeInput}
        onFeatureCreated={() => {
          setAddFeatureModalKey((prev) => prev + 1);
        }}
      />

      <DeleteConfirmModal
        open={deleteConfirmDialog.open}
        onClose={handleDeleteFeatureCancel}
        onConfirm={handleDeleteFeatureConfirm}
        featureName={
          deleteConfirmDialog.index !== null
            ? features[deleteConfirmDialog.index]?.name
            : ""
        }
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
};

export default MembershipFormSystem;
